import http from '../utils/http';
import {
  DELETE_IMAGES,
  SET_ACTION_MODE,
  SET_SUBMITTING,
  ADD_IMAGE,
  SET_IMAGES,
  ADD_NON_STANDARD_IMAGE,
  SET_PRODUCT_EVALUATION,
  SET_RECEIVED_PRODUCT,
  SET_STRIP_OBSERVATION,
  SET_TECHNICAL_INVESTIGATION,
  SET_CONFORMITY_TEST,
  SET_QUESTION_OPTIONS,
  SET_CHANGED_OPTIONS,
  SET_NON_STANDARD_IMAGES,
  SET_ADDITIONAL_IMAGES,
  SET_FILES_TO_UPLOAD,
} from '../constants/actionTypes';

import { setFeedbackAction } from './feedback';

import { setSingleJob } from '../actions/singleJob';

import {
  downloadFileUrl,
  getSAPCauseCodes,
  jobUrl,
  sectionFormUrl,
  editPictureCommentUrl,
  editPictureCommentLockedJobUrl,
  sendFaultyPartsEmailsUrl,
  faultyPartsNotifyTeamUrl,
} from '../constants/urls';

import {
  getSubsectionsAndQuestions,
} from '../components/JobSection/constants';

import { QuestionSectionNames } from '../views/SingleJob/constants';

const getFormType = section => {
  switch (section) {
    case QuestionSectionNames.RECEIVED_PRODUCT:
      return SET_RECEIVED_PRODUCT;
    case QuestionSectionNames.PRODUCT_EVALUATION:
      return SET_PRODUCT_EVALUATION;
    case QuestionSectionNames.STRIP_OBSERVATION:
      return SET_STRIP_OBSERVATION;
    case QuestionSectionNames.TECHNICAL_INVESTIGATION:
      return SET_TECHNICAL_INVESTIGATION;
    case QuestionSectionNames.CONFORMITY_TEST:
      return SET_CONFORMITY_TEST;
    default:
      return SET_RECEIVED_PRODUCT;
  }
}

const causeCodeQuestionsKeys = {
  SAPCauseCode: 'StripObservation_SAPCauseCode',
  SAPCauseCodeGroup: 'StripObservation_SAPCauseCodeGroup',
}

const setFormSubmittingAction = isSubmitting => ({ type: SET_SUBMITTING, payload: isSubmitting });

const setSectionFields = (type, fields) => ({ type, payload: fields});

const setQuestionOptions = (field, options) => ({ type: SET_QUESTION_OPTIONS, payload: { field, options } })

const setChangedOptions = fieldsArray => ({ type: SET_CHANGED_OPTIONS, payload: fieldsArray });

const setActionModeAction = isActive => ({ type: SET_ACTION_MODE, payload: isActive });

const addImageAction = (image, key) => {
  key && (image.location = key);
  return { type: ADD_IMAGE, payload: image };
};

const addNonStandardImageAction = (image, key) => {
  key && (image.location = key);
  return { type: ADD_NON_STANDARD_IMAGE, payload: image };
}

const deleteImagesActions = () => ({ type: DELETE_IMAGES });

const setImagesAction = images => ({ type: SET_IMAGES, payload: images});

const setNonStandardImagesAction = images => ({ type: SET_NON_STANDARD_IMAGES, payload: images});

const setAdditionalImagesAction = images => ({ type: SET_ADDITIONAL_IMAGES, payload: images });

const setFilesToUploadAction = character => ({ type: SET_FILES_TO_UPLOAD, payload: character})

// This is used to set global submitting flag to prevent sending multiple forms simultaneously
export const setFormSubmitting = isSubmitting => dispatch => dispatch(setFormSubmittingAction(isSubmitting));

export const getSectionQuestions = (jobId, section) =>
  async dispatch => {
    try {
      dispatch(setFormSubmitting(true))
      const { data: response } = await http.get(sectionFormUrl(jobId, section));
      const { SAPCauseCode, SAPCauseCodeGroup } = causeCodeQuestionsKeys;
      const type = getFormType(section);

      const SAPCauseCodeQuestion = response.data.filter(q => q.key === SAPCauseCode);

      if (section === QuestionSectionNames.STRIP_OBSERVATION && SAPCauseCodeQuestion.length) {
        const SAPCauseCodeGroupQuestion = response.data.filter(q => q.key === SAPCauseCodeGroup);
        const options = SAPCauseCodeGroupQuestion[0].options.map(o => o.label);
        const optionsLAbels = options.map(o => `"${o}"`);
        // Get options for SAP cause code if we have answer for SAP cause code group
        const newDataWithSAPCauseCodes = await http.get(getSAPCauseCodes(jobId) + SAPCauseCodeGroupQuestion[0].answer);

        response.data = newDataWithSAPCauseCodes.data.data;

        SAPCauseCodeQuestion[0].condition += ` && [${optionsLAbels}].includes(${SAPCauseCodeGroup})`;
        SAPCauseCodeQuestion[0].conditions[1] = {property: SAPCauseCodeGroup, values: [...options]};
      }

      const preparedData = getSubsectionsAndQuestions(response.data);

      dispatch(setSectionFields(type, preparedData));
    } catch (e) {
      dispatch(setFeedbackAction(e.message));
    } finally {
      dispatch(setFormSubmitting(false));
    }
  }

  // Send an answer, expect options for other field(s)
  export const sendAnswer = (apiEndpoint, answer, isCCodeQuestion) =>
    async dispatch => {
      try {
        const { data: response } = await http.get(apiEndpoint + answer);
        const { SAPCauseCode } = causeCodeQuestionsKeys;

        if (isCCodeQuestion) {
          dispatch(setChangedOptions([SAPCauseCode]));
          const SAPCauseCodeQuestion = response.data.find(q => q.key === SAPCauseCode);
          dispatch(setQuestionOptions(SAPCauseCode, SAPCauseCodeQuestion.options));
        }

        // Set an indicator for fields that received new options
        dispatch(setChangedOptions(Object.keys(response.data)))
        
        // Set new options
        for (let [key, value] of Object.entries(response.data)) {
          dispatch(setQuestionOptions(key, value));
        }
      } catch (e) {
        dispatch(setFeedbackAction(e.message));
      }
    }

  export const saveAnswers = (jobId, section, answers, hasTestAssessmentChanged) =>
    async dispatch => {
      try {
        dispatch(setFormSubmitting(true))
        const { SAPCauseCodeGroup } = causeCodeQuestionsKeys;
        const { data: response } = await http.post(sectionFormUrl(jobId, section), answers);
        const SAPCauseCodeGroupQuestion = response.data.filter(q => q.key === SAPCauseCodeGroup);

        // Get options for SAP cause code if we have answer for SAP cause code group
        if (SAPCauseCodeGroupQuestion.length) {
          const newDataWithSAPCauseCodes = await http.get(getSAPCauseCodes(jobId) + SAPCauseCodeGroupQuestion[0].answer);
          response.data = newDataWithSAPCauseCodes.data.data;
        }
        
        const type = getFormType(section);
        const preparedData = getSubsectionsAndQuestions(response.data);

        if (hasTestAssessmentChanged) {
          const { data: response } = await http.get(jobUrl(jobId));
          dispatch(setSingleJob(response.data))
        }

        dispatch(setSectionFields(type, preparedData));
        dispatch(setFeedbackAction('Form saved successfully'));
        sessionStorage.removeItem('questionAnswerSession');
      } catch (e) {
        dispatch(setFeedbackAction(e.message));
        console.log(e.message)
      } finally {
        dispatch(setFormSubmitting(false));
      }
    }
  
  export const sendFaultyPartsEmails = (jobId, emails) =>
    async dispatch => {
      try {
        dispatch(setFormSubmitting(true));
        await http.post(sendFaultyPartsEmailsUrl(jobId), emails);
      } catch (e) {
        dispatch(setFeedbackAction(e.message));
      } finally {
        dispatch(setFeedbackAction('Form saved successfully'));
        dispatch(setFormSubmitting(false));
      }
    }

  export const faultyPartsNotifyTeam = (jobId) =>
    async dispatch => {
      try {
        dispatch(setFormSubmitting(true));
        await http.post(faultyPartsNotifyTeamUrl(jobId));
      } catch (e) {
        dispatch(setFeedbackAction(e.message));
      } finally {
        dispatch(setFeedbackAction('Form saved successfully'));
        dispatch(setFormSubmitting(false));
      }
    }

  export const downloadFile = (jobId, fileName) =>
    async dispatch => {
      try {
        const { data: {data: response} } = await http.get(downloadFileUrl(jobId, fileName))

        const downloadLink = document.querySelector('#download');
        downloadLink.href = response;
        downloadLink.setAttribute('download', fileName);
        downloadLink.click();
        downloadLink.href = "";
      } catch (e) {
        dispatch(setFeedbackAction(e.message));
      }
    }

  export const editPictureComment = (jobId, uuid, comment, section) =>
    async dispatch => {
      try {
        const { data: response } = await http.put(editPictureCommentUrl(jobId, section), {alternativeDescription: comment, uuid});
        const preparedData = await getSubsectionsAndQuestions(response.data);
        const type = getFormType(section);

        await dispatch(setSectionFields(type, preparedData));
      } catch (e) {
        dispatch(setFeedbackAction(e.message))
      }
    }

  export const editPictureCommentLockedJob = (jobId, uuid, comment, section) =>
    async dispatch => {
      try {
        const { data: response } = await http.put(editPictureCommentLockedJobUrl(jobId, section), {alternativeDescription: comment, uuid});
        const preparedData = await getSubsectionsAndQuestions(response.data);
        const type = getFormType(section);

        await dispatch(setSectionFields(type, preparedData));
      } catch (e) {
        dispatch(setFeedbackAction(e.message))
      }
    }

  export const addImage = (image, key = null) => dispatch => dispatch(addImageAction(image, key));

  export const addNonStandardImage = (image, key = null) => dispatch => dispatch(addNonStandardImageAction(image, key));

  export const deleteImages = () => dispatch => dispatch(deleteImagesActions());

  export const setImages = images => dispatch => dispatch(setImagesAction(images));

  export const setNonStandardImages = images => dispatch => dispatch(setNonStandardImagesAction(images));

  export const setAdditionalImages = images => dispatch => dispatch(setAdditionalImagesAction(images));

  export const setFilesToUpload = character => dispatch => dispatch(setFilesToUploadAction(character));

  export const setActionMode = isActive => dispatch => dispatch(setActionModeAction(isActive));

  export const clearChangedOptions = () => dispatch => dispatch(setChangedOptions([]));

  export const clearSectionQuestions = section => dispatch => dispatch(setSectionFields(getFormType(section), null));



// WEBPACK FOOTER //
// ./src/actions/form.js